import React, { useState } from 'react';
import { Checkbox, DatePicker, Divider, Form } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useUsers } from 'hooks/Users/useUsers';
import FilterCheckBox from '../FilterCheckBox';
import FilterPopover from '../FilterPopover';
import { Button } from 'ui';

type FilterDataMap = Record<string, unknown>;

interface UsersFilterInterface {
    tagBuilder: Function;
    orgUnits: string[];
    departments: string[];
    customers: string[];
    position: string[];
    setFilterValues: any;
    filterValues: any;
    form: any;
    filterKey: string;
    setFilterKey: React.Dispatch<React.SetStateAction<string>>;
}

const UsersFilter: React.FC<UsersFilterInterface> = ({
    filterKey,
    setFilterKey,
    form,
    tagBuilder,
    orgUnits,
    departments,
    customers,
    position,
    setFilterValues,
}) => {
    const { searchUsers, loading } = useUsers();
    const [data, setData] = useState<FilterDataMap>({}); // latest search results
    const [selected, setSelected] = useState<FilterDataMap>({}); // chosen users, kept across searches
    const [initialValues, setInitialValues] = useState(undefined);

    const key = 'users';

    const onSearch = async (value: string) => {
        const response = await searchUsers(value, orgUnits, departments, customers, position);

        const safeResponse: FilterDataMap =
            response && typeof response === 'object' && !Array.isArray(response) ? response : {};

        setData(safeResponse);

        setFilterValues((prev: any) => ({
            ...prev,
            userTypeId: {
                ...prev.userTypeId,
                ...selected,
                ...safeResponse,
            },
        }));
    };

    const onSelectionChange = (ids: string[]) => {
        const known: FilterDataMap = { ...selected, ...data };
        const nextSelected = ids.reduce<FilterDataMap>((acc, id) => {
            if (id in known) acc[id] = known[id];
            return acc;
        }, {});

        setSelected(nextSelected);
        setFilterValues((prev: any) => ({
            ...prev,
            userTypeId: {
                ...prev.userTypeId,
                ...nextSelected,
            },
        }));
    };

    const content = (
        <div className={'flex flex-column p-1 checkbox-wrapper'}>
            <FilterCheckBox
                data={{ ...selected, ...data }}
                setData={onSelectionChange}
                name={'userTypeId'}
                onSearch={onSearch}
            />
            <div className={'p-1 checkbox-wrapper'}>
                <div className={'bold'}>
                    <FormattedMessage id={'users.form.account_status'} />
                </div>
                <Form.Item className={'mb-0'} name="userStatus">
                    <Checkbox.Group>
                        <Checkbox value="ACTIVE">
                            <FormattedMessage id="user.status.ACTIVE" />
                        </Checkbox>
                        <Checkbox value="BLOCKED">
                            <FormattedMessage id="user.status.BLOCKED" />
                        </Checkbox>
                        <Checkbox value="TEMPORARY_BLOCKED">
                            <FormattedMessage id='user.status.TEMPORARY_BLOCKED'/>
                        </Checkbox>
                    </Checkbox.Group>
                </Form.Item>
                <Divider plain className={'m-2'} />
                <div className={'bold'}>
                    <FormattedMessage id={'users.form.long_absence'} />
                </div>
                <Form.Item className={'mb-0'} name="absence">
                    <Checkbox.Group>
                        <Checkbox value={'true'}>
                            <FormattedMessage id="general.yes" />
                        </Checkbox>
                        <Checkbox value={'false'}>
                            <FormattedMessage id="general.no" />
                        </Checkbox>
                    </Checkbox.Group>
                </Form.Item>
                <Divider plain className={'m-2'} />
                <div className={'bold'}>
                    <FormattedMessage id={'users.form.commencement_date'} />
                </div>
                <div className={'flex flex-column checkbox-wrapper'}>
                    <Form.Item name={'commencementDate'}>
                        <DatePicker.RangePicker />
                    </Form.Item>
                </div>
                <Divider plain className={'m-2'} />
            </div>
            <div className={'flex justify-between p-1'}>
                <Button type={'link'} onClick={() => handleClose()} message="general.cancel" />
                <Button
                    type={'primary'}
                    onClick={() => {
                        setFilterKey('');
                        handleOpen(false);
                    }}
                    message="general.select"
                />
            </div>
        </div>
    );

    const handleClose = () => {
        form.setFieldsValue(initialValues);
        setFilterKey('');
    };

    const handleOpen = (open: boolean) => {
        if (open) {
            setFilterKey(key);
            setInitialValues(form.getFieldsValue());
            setFilterValues((prev: any) => ({
                ...prev,
                userTypeId: {
                    ...prev.userTypeId,
                    ...selected,
                    ...data,
                },
            }));
        } else {
            tagBuilder();
        }
    };

    return (
        <FilterPopover
            popoverKey={key}
            filterKey={filterKey}
            setOpen={setFilterKey}
            placement={'bottomRight'}
            handleOpen={handleOpen}
            content={content}
            loading={loading}
            title={'general.search_by_user'}
            buttonTitle={'general.users'}
        />
    );
};

export default UsersFilter;
